<template>
    <div data-component="Modal"
         class="modl-container"
         :class="{'modl-container--blur': blurBackdrop}"
         @click.self="backdropClick">
        <div class="modl"
             :class="classes">
            <slot name="header">
                <div class="modl-header">
                    <h4>
                        <i v-if="icon"
                           class="fa-solid"
                           :class="icon"></i>
                        <slot name="header-title">
                            <template v-if="customTitleText">
                                {{ customTitleText }}
                            </template>
                            <template v-else-if="title">
                                {{ l(title) }}
                            </template>
                        </slot>
                    </h4>
                    <i v-if="loading"
                       class="fa-solid fa-spinner fa-spin"></i>
                </div>
            </slot>

            <!-- You can put something in the "body" slot if you want to be outside the .modl-body container.
                    Otherwise, the default slot content goes inside the .modl-body. -->
            <slot name="body">
                <div ref="modal-body"
                     class="modl-body"
                     :class="{
                         'modl-body--padded': paddedBody,
                         'modl-body--shaded': shadedBody
                     }">
                    <!-- Default slot -->
                    <slot></slot>
                </div>
            </slot>

            <slot name="footer">
                <!-- Show the footer if an OK or Cancel button is to be shown. -->
                <div v-if="ok || cancel"
                     class="modl-footer">
                    <button v-if="cancel"
                            class="btn btn-lg btn-default cancel-btn modl__cancel-btn"
                            @click.prevent="cancel">
                        {{ cancelLabelRaw ? cancelLabelRaw : l(cancelLabel) }}
                    </button>
                    <button v-if="ok"
                            class="btn btn-lg ok-btn"
                            :class="[`btn-${okColor}`]"
                            :disabled="okSpinner || !okEnabled"
                            @click.prevent="ok">
                        <i v-if="okSpinner"
                           class="fa-solid fa-spinner fa-spin" />
                        <template v-else>
                            {{ okLabelRaw ? okLabelRaw : l(okLabel) }}
                        </template>
                    </button>
                </div>
            </slot>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Modal',

    props: {
        // Set the size of the modal.
        // Possible values are empty (default size), 'sm', 'lg', or 'xl'.
        size: {
            type: String,
            default: '',
        },
        height: {
            type: String,
            default: '',
        },

        // Add extra padding to the body to match the design of newer mockups?
        paddedBody: {
            type: Boolean,
            default: false,
        },

        // Shade the background of the body light grey to match the design of newer mockups?
        shadedBody: {
            type: Boolean,
            default: false,
        },

        // Icon to display in the header.
        // Give a FontAwesome icon name such as "fa-circle-exclamation"
        // This will be a .fa-solid
        icon: {
            type: String,
            default: 'fa-circle-exclamation',
        },
        // A key from the language file to use as the title in the header.
        title: {
            type: String,
            default: null,
        },
        // A string of text to display as the title in the header instead of the :title lang key.
        // Note that this is still escaped, so HTML is not allowed.
        // Overrides this.title if both are supplied.
        customTitleText: {
            type: String,
            default: null,
        },
        // If an 'ok' function is given, an OK button will be shown.
        ok: {
            type: Function,
            default: null,
        },
        okColor: {
            type: String,
            default: 'primary',
        },
        okEnabled: {
            type: Boolean,
            default: true,
        },
        // A key from the language file to use as the label for the OK button.
        okLabel: {
            type: String,
            default: 'OK',
        },
        // A raw string of text to display as the label for the OK button.
        // Overrides this.okLabel if both are supplied.
        okLabelRaw: {
            type: String,
            default: null,
        },
        // If true the OK button will show a loading spinner instead of the text and be disabled.
        okSpinner: {
            type: Boolean,
            default: false,
        },
        // If a 'cancel' function is given a Cancel button will be shown.
        cancel: {
            type: Function,
            default: null,
        },
        cancelLabel: {
            type: String,
            default: 'CANCEL',
        },
        // A raw string of text to display as the label for the cancel button.
        // Overrides this.cancelLabel if both are supplied.
        cancelLabelRaw: {
            type: String,
            default: null,
        },
        cancelOnBackdropClick: {
            type: Boolean,
            default: false,
        },

        /**
         * Show a loading indicator in the top right of the modal header?
         */
        loading: {
            type: Boolean,
            default: false,
        },

        /**
         * If true the backdrop of the modal will be darker and blur the content behind it.
         */
        blurBackdrop: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        classes() {
            let classes = [];
            if (this.size) {
                classes.push(`modl--${this.size}`);
            }
            if (this.height) {
                classes.push(`modl--${this.height}`);
            }

            return classes;
        },
    },

    methods: {
        scrollBodyTo(offset = 0) {
            this.$nextTick((() => {
                this.$refs['modal-body'].scrollTop = offset;
            }));
        },

        backdropClick() {
            if (this.cancelOnBackdropClick) {
                this.cancel();
            }
        },
    },
};
</script>

<style lang="less">
@import (reference) "../../../less/variables";
@import (reference) "../../../less/dtui/dtui";

.modl-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: @vue-modal-z-index + 1;
    padding: @gutter-width; // Provides padding between the modal and window border if the modal fills the screen.

    display: flex;
    // This could be center to vertically center the modal on the screen, but that comes with issues when the height
    // of the modal changes (such as with tabs in the modal which change the content) and it makes the modal
    // jump around.
    align-items: flex-start;
    justify-content: center;
}

.modl-container--blur {
    background: rgba(29, 29, 29, 0.6);

    // Use a darker background and blur the content behind it.
    backdrop-filter: blur(20px);

    // Bring the solid backdrop modals up higher than others.
    z-index: @vue-modal-z-index + 10;
}

.modl {
    max-height: 100%;
    max-width: 100%;
    width: @modal-width-default;

    background: @white;
    // The top left and right corners get 1px more border radius because if they have the same radius as the header
    // you see a smidge of white behind the header's corners.
    // By increasing the curve of the white bg's corners the header always comes out on top.
    border-radius: (@modal-border-radius+1) (@modal-border-radius+1) @modal-border-radius @modal-border-radius;

    display: flex;
    flex-direction: column;

    box-shadow: 0 5px 15px rgba(0, 0, 0, .5);

    &.modl--sm {
        width: @modal-width-small;
    }

    &.modl--lg {
        width: @modal-width-large;
    }

    &.modl--xl {
        width: @modal-width-xl;
    }

    &.modl--full-height {
        height: 100%;
    }
}

.modl-header {
    flex-grow: 0;
    flex-shrink: 0;
    background: @dt-navy;
    border-radius: @modal-border-radius @modal-border-radius 0 0;
    color: @modal-header-text-color;
    overflow: hidden;
    white-space: nowrap;
    min-height: @modal-header-height; // min-height not height because modl-tabs are larger
    display: flex;
    align-items: center;
    padding: 0 @gutter-width;

    i {
        color: @modal-header-icon-color;
        margin-right: 7px;

        &.fa-spinner {
            color: @dt-grey;
        }
    }

    h4 {
        flex-grow: 1;
        margin: 0;
        font-size: 17px;
        min-width: 0;
        line-height: @modal-header-height;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}

.modl--danger {
    .modl-header {
        background: @dt-dark-red;

        i {
            color: @modal-header-text-color; // Change the icon from yellow to white.
        }
    }
}

.modl--warning {
    .modl-header {
        background: @brand-warning;
        color: @btn-warning-color;

        i {
            color: @btn-warning-color;
        }
    }
}

.modl--success {
    .modl-header {
        background: @dt-dark-green;

        i {
            color: @modal-header-text-color; // Change the icon from yellow to white.
        }
    }
}

.modl-body {
    background: @modal-body-bg--white;
    flex-grow: 1;
    flex-shrink: 1;
    overflow: auto;
    padding: @gutter-width;
    font-size: 15px;
    position: relative;
    scroll-behavior: smooth;

    &--padded {
        padding: 25px;
    }

    &--shaded {
        background: @modal-body-bg--grey;
    }
}

.modl-footer {
    display: flex;
    align-items: center;
    flex-grow: 0;
    flex-shrink: 0;

    padding: @gutter-width;
    border-top: 1px solid @btn-default-border;

    .btn {
        margin: 0 !important;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    // Make the modal buttons always vertical regardless of screen size.
    &--vertical {
        display: flex;
        flex-direction: column;
        height: auto;

        .btn {
            width: 100%;
        }
    }
}

// Normally a modal is displayed like:
// .modl
//     .modl-header
//     .modl-body
//     .modl-footer
//
// But in a tabbed modal you get:
// .modl
//     .modl-header
//     .modl-tab-body
//         .modl-body
//         .modl-footer
//
// We need to make modl-tab-body play nicely with the expected flex setup
// of the modal contents.
.modl-tab-body {
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.modal__heading {
    margin-top: @double-gutter-width;
    border-bottom: 1px solid @dt-light-grey; // Same colour as <hr>
    padding-bottom: @half-gutter-width;
    margin-bottom: @half-gutter-width;

    &:first-child {
        margin-top: @half-gutter-width;
    }
}

@media (max-width: @mobile-max) {
    .modl-container {
        padding: @half-gutter-width;
    }

    .modl-header {
        min-height: 49px; // Taking off 1px makes the vertical centering better.
    }

    .modl-body {
        padding: @gutter-width-mobile;

        &--padded {
            padding: 20px;
        }
    }

    .modl-footer {
        padding: @gutter-width-mobile;
        display: flex;
        // Display buttons in a column. Reverse the order because the most important ones are normally on the right.
        flex-direction: column-reverse;
        grid-gap: @gutter-width-mobile;

        .btn {
            margin: 0 !important;
            width: 100%;
            max-width: 100%;
        }

        .cancel-btn {
            // Move the cancel button to be the last button in the list.
            order: -100;
        }
    }
}

@media (min-width: @non-mobile-min) {
    .modl-footer {
        display: flex;
        grid-gap: @gutter-width; // Spacing between footer buttons.

        &:not(.modl-footer--vertical) {
            height: @modal-footer-height;
            justify-content: flex-end; // Use the .cancel-btn class to but the left button on the left.

            .btn {
                flex-grow: 0;
                flex-shrink: 0;
                width: auto;
            }

            .cancel-btn {
                // This makes the cancel button be all the way on the left on desktop.
                // It doesn't affect the styling, only the position.
                margin-right: auto !important;
            }
        }
    }
}
</style>
